import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { __ } from '@wordpress/i18n';
// components
import Button from "../../../common/components/inputs/Button";
import Dropdown from "../../../common/components/inputs/Dropdown";
import TextField from "../../../common/components/inputs/TextField";
import Title from "../../../common/components/Title";
import SubTitle from "../../../common/components/SubTitle";
import Table from "./components/Table";
import Pagination from "../../../common/components/Pagination";

const Categories = ({page}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [flatCategories, setFlatCategories] = useState([]);
    const [categoriesData, setCategoriesData] = useState({ categories: [{ category_name: '', category_slug: '', parent: 0 }] });
    const [editingCategory, setEditingCategory] = useState(null);
    const [selectParent, setSelectParent] = useState({ label: 'None', value: 0 }); 
    const [totalPage, setTotalPage] = useState(1);
    const [postPerPage, setPostPerPage] = useState(10);

    useEffect(() => {
        fetchData();
    }, []);

    const generateSlug = (text) => {
        return text
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9\-]/g, '');
    };

    const flattenCategories = (categories, parentName = 'None', parentId = 0) => {
        let flatList = [];

        categories.forEach((category) => {
            flatList.push({
                id: category.id,
                name: category.name,
                slug: category.slug,
                parentName,
                parent: parentId,
            });

            if (category.children && category.children.length > 0) {
                flatList = flatList.concat(flattenCategories(category.children, category.name, category.id));
            }
        });

        return flatList;
    };

    const fetchData = async () => {
        setIsLoading(true);

        try {
            const response = await fetch(`${EASYCOMMERCE.rest_base}/products/categories?page=${page}&per_page=${postPerPage}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': EASYCOMMERCE.nonce,
                },
            });
            const data = await response.json();
            setCategories(data.data.categories);
            setTotalPage(data.data.pagination.total_pages);

            const flattened = flattenCategories(data.data.categories);
            setFlatCategories(flattened);

        } catch (error) {
            toast.error('Failed to fetch categories.');
        }finally {
            setIsLoading(false); 
        } 
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'category_name') {
            const newSlug = generateSlug(value);
            setCategoriesData((prev) => ({
                ...prev,
                categories: [{ ...prev.categories[0], category_name: value, category_slug: newSlug, parent: prev.categories[0].parent }]
            }));
        }
    };

    const handleParentChange = (option) => {
        setCategoriesData((prev) => ({
            ...prev,
            categories: [{ ...prev.categories[0], parent: option.value }]
        }));
        setSelectParent(option);
    };

    const handleEditCategory = (id) => {
        easycommerce_modal(true);
    
        const category  = findCategoryById(categories, id);
        const flatEntry = flatCategories.find(fc => fc.id === id);
    
        if (category && flatEntry) {
            setCategoriesData({
                categories: [{
                    category_name: category.name,
                    category_slug: category.slug,
                    parent: flatEntry.parent 
                }]
            });
            if (flatEntry.parent && flatEntry.parent !== 0) {
                const parentCategory = flatCategories.find(fc => fc.id === flatEntry.parent);
                if (parentCategory) {
                    setSelectParent({ label: parentCategory.name, value: parentCategory.id });
                } else {
                    setSelectParent({ label: 'None', value: 0 });
                }
            } else {
                setSelectParent({ label: 'None', value: 0 });
            }
    
            setEditingCategory(id);
        }
        easycommerce_modal(false);
    };
    
    const findCategoryById = (categories, id) => {
        for (let category of categories) {
            if (category.id === id) return category;
            if (category.children && category.children.length > 0) {
                const found = findCategoryById(category.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    const handleSubmit = async () => {
        const payload = {
            name: categoriesData.categories[0].category_name,
            slug: categoriesData.categories[0].category_slug,
            parent: categoriesData.categories[0].parent
        };

        if (!payload.name.trim() || !payload.slug.trim()) {
            toast.error('All fields are required!');
            return;
        }
        const method = editingCategory ? 'PUT' : 'POST';

        try {
            const url = editingCategory
                ? `${EASYCOMMERCE.rest_base}/products/categories/${editingCategory}`
                : `${EASYCOMMERCE.rest_base}/products/categories`;

            easycommerce_modal(true);
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': EASYCOMMERCE.nonce,
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.success) {
                toast.success(`Category ${editingCategory ? 'updated' : 'created'} successfully`);
                fetchData();
                setCategoriesData({ categories: [{ category_name: '', category_slug: '', parent: 0 }] });
                setEditingCategory(null);
                setSelectParent({ label: 'None', value: 0 });
                easycommerce_modal(false);
            } else {
                toast.error(data.data.message || 'Operation failed');
                easycommerce_modal(false);
            }
        } catch (error) {
            toast.error('Operation failed');
        }
    };

    const findAllDescendantIds = (categories, id) => {
        let descendantIds = [];
    
        categories.forEach(category => {
            if (category.parent === id) {
                descendantIds.push(category.id);
                descendantIds = descendantIds.concat(findAllDescendantIds(categories, category.id));
            }
        });
    
        return descendantIds;
    };
    

    const descendantIds = editingCategory ? findAllDescendantIds(flatCategories, editingCategory) : [];

    const selectedParentOptions = [
        { label: 'None', value: 0 },
        ...flatCategories
            .filter(cat => cat.id !== editingCategory && !descendantIds.includes(cat.id))
            .map(cat => ({ label: cat.name, value: cat.id }))
    ];

    const handleCancel = () => {
        easycommerce_modal(true);
 
        setCategoriesData({ categories: [{ category_name: '', category_slug: '', parent: 0 }] });
        setEditingCategory(null);
        setSelectParent({ label: 'None', value: 0 });
     
        setTimeout(() => {
            easycommerce_modal(false);
        }, 100); 
    };
    

    return (
        <>
            <div className="product-panel-title mb-4">
                <h3>Categories</h3>
            </div>
            <div className="grid min-[1400px]:grid-cols-10 min-[1280px]:grid-cols-12 gap-6 items-start">
                <div className="bg-white min-[1400px]:col-span-6 min-[1280px]:col-span-12 w-full self-start border border-solid border-ec-table-stock rounded-xl">
                    <div className="flex items-center border-b pl-6 py-3.5">
                        <Title title="Categories List" />
                    </div>

                    <div className="p-6">
                        <Table
                            categories={flatCategories}
                            fetchData={fetchData}
                            onEdit={handleEditCategory}
                            isLoading={isLoading}
                        />
                        {totalPage > 1 && (
                            <Pagination
                                baseSlug="categories"
                                current={page}
                                total={totalPage}
                            />
                        )}
                    </div>
                </div>

                {/* Form Section */}
                <div className="bg-white min-[1400px]:col-span-4 min-[1280px]:col-span-12 w-full self-start border border-solid border-ec-table-stock rounded-xl">
                    <div className="flex items-center border-b pl-6 py-3.5">
                        <Title title={editingCategory ? 'Edit Category: ' + categoriesData.categories[0].category_name : "Add New Category"} />
                    </div>

                    <div className="p-6 flex flex-col gap-4">
                        <div className='flex flex-col gap-2'>
                            <SubTitle SubTitle="Name" notice="It'll be used to filter products" />
                            <TextField 
                                name="category_name"
                                value={categoriesData.categories[0].category_name}
                                onChange={handleChange} 
                                placeholder="Enter Name"
                                className="h-ec-input"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <SubTitle SubTitle="Slug" notice="URL-friendly slug of the category" />

                            <div className="h-ec-input rounded-lg font-inter text-[14px] leading-[20px] border border-ec-table-stock placeholder-ec-placeholder hover:border-ec-primary focus-within:border-ec-primary focus-within:outline-none focus-within:[box-shadow:0_0_0_4px_#F3F0FF] transition-colors duration-300 ease-in-out overflow-hidden flex ">
                                <div className="h-ec-input flex items-center justify-center text-ec-light-black pl-4">
                                    {EASYCOMMERCE.home_url}/shop/categories/
                                </div>
                                <input
                                    type="text"
                                    name="category_slug"
                                    className="h-ec-input border-none outline-none shadow-none p-0 text-ec-body font-inter text-[14px] leading-[20px]"
                                    // placeholder={__('Enter Slug', 'easycommerce')}
                                    value={categoriesData.categories[0].category_slug}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setCategoriesData(prev => ({
                                        ...prev,
                                        categories: [{ ...prev.categories[0], category_slug: val }]
                                        }));
                                    }}
                                />
                            </div>
                        </div>


                        <div className='flex flex-col gap-2'>
                            <SubTitle SubTitle="Parent" notice="Select a parent category if any" />
                            <div className="w-full h-ec-input">
                                <Dropdown
                                    key={editingCategory || 'new'}
                                    options={selectedParentOptions}
                                    placeholder="Select Parent"
                                    onChange={handleParentChange}
                                    value={selectParent.value}
                                />
                            </div>
                        </div>

                        <div className="flex justify-between mt-6">
                            {editingCategory ? (
                                <Button
                                    className="h-[37px] mt-[15px] easycommerce-underline-button"
                                    value="Cancel"
                                    onClick={handleCancel} 
                                />
                            ) : (
                                <div /> 
                            )}
                            <Button
                                className="easycommerce-outline-button"
                                value={editingCategory ? "Update Category" : "Create Category"}
                                onClick={handleSubmit}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Categories;
